import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Fragment, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowDown, ArrowUp, CaretDown, CaretRight, ChartLine, FlowArrow, ListBullets, WarningCircle } from "@phosphor-icons/react";
import { ResponsiveLine } from "@nivo/line";
import { ResponsiveSankey } from "@nivo/sankey";
import { axisBaselineLayer, chartTheme } from "../Consumption/chartTheme";
import { DimensionDropdown, QuarterDropdown, YearDropdown } from "../FootPrint";
import * as React from "react";
import { toSafariSafeSankey } from "../../../../../utils/sankey";
import { isMixedScopeTwoElectricity, v1PurchasedElectricityKwh } from "../../../../../utils/scopeTwo";
import { useBreakdownLevel } from "../../../../../hooks/useBreakdownLevel";
import {
  BY_DIMENSION_PAGE_SIZE,
  QUARTERLY_BY_DIMENSION_PAGE_SIZE,
  dimensionFilterParams,
} from "../../../../../utils/breakdownLevel";
import { useGetCompanyDimensionReports, useGetQuarterlyCompanyDimensionReports } from "../../../../../hooks/query/reports";
import {
  useGetMultiYearEmissions,
  useGetQuarterlyEmissions,
  useGetQuarterlyMultiYearEmissions,
} from "../../../../../hooks/query/emissions-insight/emissions-insights-query";
import SourceDataTasks from "./SourceDataTasks";
import QuartersSwitch, { quarterPeriodLabels } from "./QuartersSwitch";
import QuarterlyBreakdownTable, { useQuarterlyTableCompare } from "./QuarterlyBreakdownTable";
import JumpToScope from "./JumpToScope";
import ExpandCollapseControl from "./ExpandCollapseControl";

// Scope 2 v2 purchased-electricity category codes → display labels (matches the
// backend ReportElectricityV2Item category enum + ONSITE summary row).
const V2_CATEGORY_LABELS = {
  STANDARD_GRID: "Standard Grid",
  SUPPLIER_CONTRACTED: "Supplier Contracted",
  CERTIFIED_RENEWABLE: "Certified Renewable",
  DIRECT_PRIVATE_WIRE: "Direct / Private Wire",
  ONSITE: "On-site generation",
};
const V2_CATEGORY_COLORS = {
  STANDARD_GRID: "#64748B",
  SUPPLIER_CONTRACTED: "#0EA5E9",
  CERTIFIED_RENEWABLE: "#84CC16",
  DIRECT_PRIVATE_WIRE: "#F59E0B",
  ONSITE: "#14B8A6",
};

const formatTco2 = (value) => {
  if (value == null) return null;
  return value.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatKwh = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return Number(value).toLocaleString("en-GB", { maximumFractionDigits: 0 });
};

const sumKwh = (rows) =>
  rows.reduce((acc, r) => acc + (Number(r.consumption) || 0), 0);

// Walk the electricity rows for a given backend type prefix (e.g. "LOCATION",
// "MARKET", "RENEWABLE") and return the kWh total, defending against unknown
// future enum values by falling back to substring match on consumptionType.
const totalKwhFor = (rows, matcher) =>
  sumKwh((rows || []).filter((r) => matcher(String(r.consumptionType || ""))));

// Year-on-year % change between a current and previous value. Returns null
// when it can't be computed (missing/zero previous), so callers can omit the
// chip rather than show a misleading 0%.
const pctChange = (current, previous) => {
  if (typeof current !== "number" || typeof previous !== "number" || previous === 0) return null;
  return ((current - previous) / previous) * 100;
};

// Inline YoY chip — a small ↓/↑ arrow + percentage shown beside a number.
// `lowerIsBetter` (default true, e.g. emissions/consumption) colours a
// decrease green; pass false to flip it. Renders nothing when delta is null.
const YoYDelta = ({ current, previous, lowerIsBetter = true }) => {
  const delta = pctChange(current, previous);
  if (delta == null) return null;
  const isGood = lowerIsBetter ? delta < 0 : delta > 0;
  return (
    <Text as="span" fontSize="11px" color={isGood ? "green.600" : "red.600"} fontWeight="600" ml={2}>
      {delta < 0 ? "↓" : "↑"} {Math.abs(delta).toFixed(1)}%
    </Text>
  );
};

// Dual share bar — overlays the location-based and market-based shares of
// Scope 2 in a single track. Blue = location, green = market. The two bars are
// drawn from the same left edge; whichever share is smaller is rendered on top
// so both remain visible. Percentages are shown beside, colour-matched.
const DualShareBar = ({ locationShare, marketShare, size = "md" }) => {
  const locPct = Math.max(0, Math.min(100, (locationShare || 0) * 100));
  const mktPct = Math.max(0, Math.min(100, (marketShare || 0) * 100));
  const locOnTop = locPct <= mktPct;
  const trackH = size === "sm" ? "5px" : "6px";
  const labelSize = size === "sm" ? "12px" : "13px";
  return (
    <HStack spacing={3}>
      <Box position="relative" flex="1" h={trackH} bg="gray.100" borderRadius="full" overflow="hidden">
        {/* Larger share behind, smaller share on top. */}
        <Box position="absolute" left={0} top={0} h="100%" w={`${Math.max(locPct, mktPct)}%`} bg={locOnTop ? MARKET_COLOR : LOCATION_COLOR} borderRadius="full" />
        <Box position="absolute" left={0} top={0} h="100%" w={`${Math.min(locPct, mktPct)}%`} bg={locOnTop ? LOCATION_COLOR : MARKET_COLOR} borderRadius="full" />
      </Box>
      <HStack spacing={2} minW="96px" justify="flex-end">
        <Text fontSize={labelSize} color={LOCATION_COLOR} fontWeight="600">{locPct.toFixed(1)}%</Text>
        <Text fontSize={labelSize} color="gray.300">/</Text>
        <Text fontSize={labelSize} color={MARKET_COLOR} fontWeight="600">{mktPct.toFixed(1)}%</Text>
      </HStack>
    </HStack>
  );
};

// Small legend explaining the dual share bar's two colours.
const ShareLegend = () => (
  <HStack spacing={3} fontSize="11px" color="gray.500">
    <HStack spacing={1.5}>
      <Box w="10px" h="10px" borderRadius="sm" bg={LOCATION_COLOR} />
      <Text>Location</Text>
    </HStack>
    <HStack spacing={1.5}>
      <Box w="10px" h="10px" borderRadius="sm" bg={MARKET_COLOR} />
      <Text>Market</Text>
    </HStack>
  </HStack>
);

const ScopeTwoDetail = ({
  currentYearData,
  previousYearData,
  isFetchingCurrentYear,
  isFetchingPreviousYear,
  year,
  setYear,
  quarter,
  setQuarter,
  dimension,
  setDimension,
  dimensionParams,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const handleBack = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("detailView");
    setSearchParams(params);
  };

  // Headline KPI cards read the flat V1 electricity list for kWh footers.
  // V2 orgs get nothing here — InsightsFacade only maps ReportElectricityItem
  // (old ElectricityType enum) into scopeOneAndTwoDetail.electricity — so the
  // v2 per-category kWh (computed below) takes precedence when present.
  // Memoised so its identity is stable for the mixed-electricity useMemos below.
  const electricity = useMemo(
    () => currentYearData?.scopeOneAndTwoDetail?.electricity || [],
    [currentYearData],
  );
  const isLocation = (t) => t.toUpperCase().includes("LOCATION");
  const isRenewable = (t) => t.toUpperCase().includes("RENEWABLE");

  const v1LocationKwh = totalKwhFor(electricity, isLocation);
  const v1RenewableKwh = totalKwhFor(electricity, isRenewable);

  const locationTco2 = currentYearData?.scope2LocationTotal;
  const marketTco2 = currentYearData?.scope2MarketTotal;
  const prevLocationTco2 = previousYearData?.scope2LocationTotal;
  const prevMarketTco2 = previousYearData?.scope2MarketTotal;

  // "Avoided emissions" = the gap between the location-based grid factor
  // and the market-based residual after renewables are netted out.
  const offsetTco2 = (locationTco2 != null && marketTco2 != null)
    ? Math.max(0, locationTco2 - marketTco2)
    : null;
  const prevOffsetTco2 = (prevLocationTco2 != null && prevMarketTco2 != null)
    ? Math.max(0, prevLocationTco2 - prevMarketTco2)
    : null;

  // ----- Real data layer (ESG-3935) -------------------------------------
  const reportQuarter = quarter || "ALL";

  // ESG-3960: the level the By-dimension table breaks down into — shared with the other drill-downs.
  const {
    organizationStructure,
    autoLevelKey,
    levelKey: nextLevelKey,
    levelDimension: nextLevelDimension,
    levelName: dimensionLevelName,
    levelOptions,
    onSelectLevel,
  } = useBreakdownLevel(dimension);

  // ESG-3960: includeUnattributed adds one row carrying the Scope 2 that carries no dimension item at
  // this level, so the table reconciles with the KPI card instead of quietly omitting it.
  const { data: dimensionReport, isFetching: isFetchingDimension } = useGetCompanyDimensionReports(
    nextLevelDimension?.id,
    {
      year,
      quarter: reportQuarter,
      page: 0,
      size: BY_DIMENSION_PAGE_SIZE,
      includeUnattributed: true,
      ...dimensionFilterParams(dimension),
    },
    { enabled: !!nextLevelDimension?.id && !!year }
  );

  // ESG-3960: a name-matched client-side guard used to live here, because the backend's filtered
  // recompute overwrote the user's own selection at the breakdown level. The backend now intersects.

  // Per-dimension rows: real location + market + summed v2 kWh per item, plus
  // the item's own per-category v2 breakdown for the expandable accordion.
  const dimensionRows = useMemo(() => {
    const content = (dimensionReport?.content || []).filter(
      (row) => !row.unattributed && row.dimensionItemId != null
    );
    return content
      .map((row) => {
        const v2 = row.electricityV2 || [];
        const kwh = v2.reduce((acc, c) => acc + (Number(c.consumptionKwh) || 0), 0);
        const byCategory = {};
        v2.forEach((c) => {
          const k = c.category;
          if (!byCategory[k]) byCategory[k] = { category: k, location: 0, market: 0, kwh: 0 };
          byCategory[k].location += Number(c.locationCo2Emissions) || 0;
          byCategory[k].market += Number(c.marketCo2Emissions) || 0;
          byCategory[k].kwh += Number(c.consumptionKwh) || 0;
        });
        const categories = Object.values(byCategory)
          .filter((c) => c.kwh > 0 || c.location > 0 || c.market > 0)
          .sort((a, b) => b.location - a.location);
        return {
          id: row.dimensionItemId,
          name: row.dimensionItem,
          location: Number(row.scopeTwoLocationBasedCo2Emissions ?? row.scopeTwoTotalCo2Emissions) || 0,
          market: Number(row.scopeTwoMarketBasedCo2Emissions) || 0,
          kwh,
          categories,
        };
      })
      .filter((r) => r.location > 0 || r.market > 0)
      .sort((a, b) => b.location - a.location);
  }, [dimensionReport]);

  // The "no {level} assigned" Scope 2 figure, or null when everything is tagged at this level.
  const unattributedScopeTwo = useMemo(() => {
    const footer = (dimensionReport?.content || []).find((row) => row.unattributed);
    const value = Number(footer?.scopeTwoLocationBasedCo2Emissions ?? footer?.scopeTwoTotalCo2Emissions) || 0;
    return value > 0 ? value : null;
  }, [dimensionReport]);

  const dimensionTotalCount = dimensionReport?.totalElements ?? null;

  // ---- Quarterly by-dimension view (mirrors Scope 1) -------------------
  // Toggle lives on the page so its queries only fire when the view is on.
  // Period index 0..3 = Q1..Q4, 4 = whole-year ("ALL"). Scope 2 has REAL
  // per-dimension location/market/kWh (electricityV2) per period, so the
  // quarterly figures are exact — no apportionment needed (unlike Scope 1).
  const [dimQuarterly, setDimQuarterly] = useState(false);
  const quarterlyDimReports = useGetQuarterlyCompanyDimensionReports(
    nextLevelDimension?.id,
    { year, page: 0, size: QUARTERLY_BY_DIMENSION_PAGE_SIZE, ...dimensionFilterParams(dimension) },
    { enabled: !!nextLevelDimension?.id && !!year && dimQuarterly, retry: false, cacheTime: 60_000 }
  );
  const isFetchingQuarterlyDim = dimQuarterly && quarterlyDimReports.some((q) => q.isFetching);

  // Per-item quarterly rows. Each item carries a 5-slot period array of
  // {location, market, kwh} plus a per-electricity-category roll-down (each
  // category with its own 5-slot period array). Built by folding each
  // period's dimension report into an ID-keyed accumulator (ESG-3960 — keyed by name, two same-name
  // items were folded into one row carrying the sum of both).
  const quarterlyRows = useMemo(() => {
    if (!dimQuarterly) return [];
    const byId = new Map();
    const ensure = (id, name) => {
      const key = String(id);
      if (!byId.has(key)) {
        byId.set(key, {
          id,
          name,
          periods: Array.from({ length: 5 }, () => ({ location: 0, market: 0, kwh: 0 })),
          catMap: new Map(),
        });
      }
      return byId.get(key);
    };
    quarterlyDimReports.forEach((q, pi) => {
      (q.data?.content || []).forEach((row) => {
        if (row.unattributed || row.dimensionItemId == null) return;
        const rec = ensure(row.dimensionItemId, row.dimensionItem);
        rec.periods[pi] = {
          location: Number(row.scopeTwoLocationBasedCo2Emissions ?? row.scopeTwoTotalCo2Emissions) || 0,
          market: Number(row.scopeTwoMarketBasedCo2Emissions) || 0,
          kwh: (row.electricityV2 || []).reduce((acc, c) => acc + (Number(c.consumptionKwh) || 0), 0),
        };
        (row.electricityV2 || []).forEach((c) => {
          if (!rec.catMap.has(c.category)) {
            rec.catMap.set(c.category, {
              category: c.category,
              periods: Array.from({ length: 5 }, () => ({ location: 0, market: 0, kwh: 0 })),
            });
          }
          const slot = rec.catMap.get(c.category).periods[pi];
          slot.location += Number(c.locationCo2Emissions) || 0;
          slot.market += Number(c.marketCo2Emissions) || 0;
          slot.kwh += Number(c.consumptionKwh) || 0;
        });
      });
    });
    return [...byId.values()]
      .map((rec) => ({
        id: rec.id,
        name: rec.name,
        periods: rec.periods,
        categories: [...rec.catMap.values()]
          .filter((c) => c.periods.some((p) => p.location > 0 || p.market > 0 || p.kwh > 0))
          .sort((a, b) => (b.periods[4]?.location || 0) - (a.periods[4]?.location || 0)),
      }))
      .filter((r) => r.periods.some((p) => p.location > 0 || p.market > 0))
      .sort((a, b) => (b.periods[4]?.location || 0) - (a.periods[4]?.location || 0));
  }, [dimQuarterly, quarterlyDimReports]);

  // Org-wide v2 per-category breakdown = sum of each item's electricityV2.
  // Empty for v1 orgs → the v2 sections show a "Coming soon" state.
  const v2Categories = useMemo(() => {
    const content = dimensionReport?.content || [];
    const byCategory = {};
    content.forEach((row) => {
      (row.electricityV2 || []).forEach((c) => {
        const k = c.category;
        if (!byCategory[k]) byCategory[k] = { category: k, location: 0, market: 0, kwh: 0 };
        byCategory[k].location += Number(c.locationCo2Emissions) || 0;
        byCategory[k].market += Number(c.marketCo2Emissions) || 0;
        byCategory[k].kwh += Number(c.consumptionKwh) || 0;
      });
    });
    return Object.values(byCategory);
  }, [dimensionReport]);
  const hasV2 = v2Categories.length > 0;
  const onsiteCategory = useMemo(() => v2Categories.find((c) => c.category === "ONSITE") || null, [v2Categories]);

  // Mixed v1/v2 electricity in the selected slice: this page shows the v2
  // per-category breakdown + kWh and HIDES the v1 electricity ("v2 wins"). The
  // tCO2e headline still sums both (server-side), but the kWh footers + category
  // table drop the v1 portion — warn and quantify it so the user isn't misled.
  // Shares the detector with the Consumption tab (v2Categories uses `kwh`, so map
  // it to the `consumptionKwh` shape the helper expects).
  const mixedV1V2Electricity = useMemo(
    () =>
      isMixedScopeTwoElectricity(
        electricity,
        v2Categories.map((c) => ({ category: c.category, consumptionKwh: c.kwh })),
      ),
    [electricity, v2Categories],
  );
  const hiddenV1ElecKwh = useMemo(
    () => (mixedV1V2Electricity ? v1PurchasedElectricityKwh(electricity) : 0),
    [mixedV1V2Electricity, electricity],
  );

  // KPI footer kWh: prefer the v2 category sums when the org is on Scope 2 v2.
  // "Purchased" excludes ONSITE (self-generated, not bought); renewable is the
  // CERTIFIED_RENEWABLE category. V1 orgs fall back to the flat electricity list.
  const v2PurchasedKwh = v2Categories
    .filter((c) => c.category !== "ONSITE")
    .reduce((acc, c) => acc + c.kwh, 0);
  const v2RenewableKwh = v2Categories.find((c) => c.category === "CERTIFIED_RENEWABLE")?.kwh || 0;

  const locationKwh = hasV2 ? v2PurchasedKwh : v1LocationKwh;
  const renewableKwh = hasV2 ? v2RenewableKwh : v1RenewableKwh;
  const renewableShare = locationKwh > 0 ? renewableKwh / locationKwh : 0;

  // Org-wide per-certificate-instrument breakdown (sum each item's certificates).
  // Used for the Sankey's 4th tier under Certified Renewable — valued by kWh
  // (per-instrument market emissions aren't decomposable; backend omits them).
  const certificates = useMemo(() => {
    const content = dimensionReport?.content || [];
    const byCert = {};
    content.forEach((row) => {
      (row.certificates || []).forEach((c) => {
        const k = c.certificateType;
        if (!byCert[k]) byCert[k] = { certificateType: k, kwh: 0, location: 0 };
        byCert[k].kwh += Number(c.consumptionKwh) || 0;
        byCert[k].location += Number(c.locationCo2Emissions) || 0;
      });
    });
    return Object.values(byCert).filter((c) => c.kwh > 0);
  }, [dimensionReport]);
  const hasCertificates = certificates.length > 0;

  // Real 5-year trend. All five years come from the same /insights/total endpoint
  // the KPI cards use (EmissionInsightDto has the location/market split that
  // /reports/total-emissions lacks). Current + previous are cache hits off
  // FootPrint's identical queryKey ["emissionTotal", dimensionParams, year, quarter]
  // — so pass RAW `quarter` (not reportQuarter) and the same dimensionParams string.
  // Years with no report 404 and are silently omitted. Mirrors EmissionsTrends.jsx.
  const trendYears = useMemo(() => [year - 4, year - 3, year - 2, year - 1, year], [year]);
  const trendQueries = useGetMultiYearEmissions(
    trendYears,
    dimensionParams,
    {
      onError: (error) => {
        if (error?.response?.status === 404) return;
      },
      retry: false,
      cacheTime: 60,
      enabled: !!year,
    },
    quarter
  );
  const trendByYear = useMemo(() => {
    const out = {};
    trendQueries.forEach((q, i) => {
      const d = q.data;
      if (d) {
        // Leave fields raw (no `|| 0`) so a year with no figure is omitted by
        // buildTrendSeries, not drawn as a spurious zero.
        out[trendYears[i]] = { location: d.scope2LocationTotal, market: d.scope2MarketTotal };
      }
    });
    return out;
  }, [trendQueries, trendYears]);

  // Quarterly trend mode: one point per quarter. Queries only fire while the
  // switch is on; the page's quarter filter is ignored (all four quarters are
  // always plotted). Mirrors EmissionsTrends.jsx / ScopeOneDetail.
  const [trendQuarters, setTrendQuarters] = useState(false);
  const quarterlyTrendQueries = useGetQuarterlyMultiYearEmissions(trendYears, dimensionParams, {
    enabled: !!year && trendQuarters,
    onError: (error) => {
      if (error?.response?.status === 404) return;
    },
    retry: false,
    cacheTime: 60,
  });
  const trendByQuarter = useMemo(() => {
    const out = {};
    const labels = quarterPeriodLabels(trendYears);
    quarterlyTrendQueries.forEach((q, i) => {
      const d = q.data;
      if (d) {
        out[labels[i]] = { location: d.scope2LocationTotal, market: d.scope2MarketTotal };
      }
    });
    return out;
  }, [quarterlyTrendQueries, trendYears]);

  return (
    <Flex direction="column" gap={6}>
      <Flex justifyContent="space-between" alignItems="center" gap={3} flexWrap="wrap">
        <Button leftIcon={<ArrowLeft size={18} />} variant="plain" colorScheme="gray" onClick={handleBack}>
          Footprint
        </Button>
        <JumpToScope current="scopeTwo" />
      </Flex>

      <Flex justify="space-between" align="flex-start" gap={4} flexWrap="wrap">
        <Box>
          <Text fontSize="11px" fontWeight="700" letterSpacing="0.06em" color="limeShade.600" textTransform="uppercase" mb={1}>
            Purchased electricity
          </Text>
          <Text fontSize="40px" fontWeight="700" color="darkTeal" lineHeight="1.1">
            Scope 2 - Indirect Emissions
          </Text>
        </Box>
        <Flex gap={2} mt={2}>
          {setDimension && <DimensionDropdown selected={dimension || {}} setSelected={setDimension} />}
          {setYear && <YearDropdown setYear={setYear} selectedYear={year} />}
          {setQuarter && <QuarterDropdown setQuarter={setQuarter} selectedQuarter={quarter} />}
        </Flex>
      </Flex>

      {mixedV1V2Electricity && (
        <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" px={4} py={3}>
          <HStack spacing={2} align="flex-start">
            <Box color="orange.600" mt="2px"><WarningCircle size={16} weight="fill" /></Box>
            <Text fontSize="13px" color="orange.800">
              This period mixes Scope 2 v2 and legacy (v1) electricity questionnaires. The kWh figures and the
              per-category breakdown on this page reflect the v2 questionnaires only
              {hiddenV1ElecKwh > 0 ? ` — about ${formatKwh(hiddenV1ElecKwh)} kWh of legacy electricity is not shown here` : ""}.
              The location- and market-based emission totals (tCO₂e) still include both.
            </Text>
          </HStack>
        </Box>
      )}

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={6}>
        <KpiCard
          accent="pureBlack"
          title="Location-based"
          subtitle="Grid-average factor"
          tco2e={locationTco2}
          previousTco2e={prevLocationTco2}
          footerLeading="From"
          footerKwh={locationKwh}
          footerTrailing="kWh purchased"
          loading={isFetchingCurrentYear || isFetchingPreviousYear}
          kind="emission"
        />
        <KpiCard
          accent="lime"
          title="Market-based"
          subtitle="Contract / supplier"
          tco2e={marketTco2}
          previousTco2e={prevMarketTco2}
          footerLeading="From"
          footerKwh={locationKwh}
          footerTrailing="kWh purchased"
          loading={isFetchingCurrentYear || isFetchingPreviousYear}
          kind="emission"
        />
        <KpiCard
          accent="lime"
          title="Renewable offset"
          subtitle="Avoided emissions"
          tco2e={offsetTco2}
          previousTco2e={prevOffsetTco2}
          footerKwh={renewableKwh}
          footerTrailing={`kWh renewable · ${(renewableShare * 100).toFixed(0)}% of total`}
          loading={isFetchingCurrentYear || isFetchingPreviousYear}
          kind="offset"
        />
      </SimpleGrid>

      <Box mt={6}>
        <ScopeTwoVisualisation
          latestYear={year}
          trendByYear={trendByYear}
          trendByQuarter={trendByQuarter}
          trendQuarters={trendQuarters}
          setTrendQuarters={setTrendQuarters}
          v2Categories={v2Categories}
          hasV2={hasV2}
          certificates={certificates}
          hasCertificates={hasCertificates}
          dimensionParams={dimensionParams}
        />
      </Box>

      <Box mt={6}>
        <ByDimensionTable
          rows={dimensionRows}
          levelName={dimensionLevelName}
          unattributed={unattributedScopeTwo}
          shownCount={dimensionRows.length}
          totalCount={dimensionTotalCount}
          loading={isFetchingDimension}
          hasNextLevel={!!nextLevelDimension?.id || levelOptions.length > 0}
          levelOptions={levelOptions}
          selectedLevelKey={nextLevelKey}
          autoLevelKey={autoLevelKey}
          onSelectLevel={onSelectLevel}
          quarterly={dimQuarterly}
          setQuarterly={setDimQuarterly}
          quarterlyRows={quarterlyRows}
          quarterlyLoading={isFetchingQuarterlyDim}
          year={year}
        />
      </Box>

      <Box mt={6}>
        <PurchasedTypeTable categories={v2Categories} hasV2={hasV2} certificates={certificates} />
      </Box>

      {onsiteCategory && (
        <Box mt={6}>
          <OnsiteInScopeTwoHighlight
            onsite={onsiteCategory}
            onOpenConsumption={() => {
              // Leave the drill-down and open the footprint Consumption tab,
              // preserving year / quarter / dimension.
              const params = new URLSearchParams(searchParams);
              params.delete("detailView");
              params.set("tab", "consumption");
              setSearchParams(params);
            }}
          />
        </Box>
      )}

      <SourceDataTasks
        scope="SCOPE_2"
        year={year}
        quarter={quarter}
        dimension={dimension}
        organizationStructure={organizationStructure}
      />
    </Flex>
  );
};

// 5-year trend of Scope 2 emissions — Location-based vs Market-based. All
// points are REAL: the current/previous years come from the page totals and
// the three older years are fetched per-year (ESG-3935). Years with no
// finalised report are simply omitted from the line.
const LOCATION_COLOR = "#1A5276";
const MARKET_COLOR = "#84CC16";

// `periods` = ordered x labels (years, or "Q1 2021"-style quarters);
// `trendByPeriod` = {periodLabel: {location, market}} keyed the same way.
const buildTrendSeries = (latestYear, trendByPeriod, showQuarters) => {
  const endYear = Number(latestYear) || new Date().getFullYear();
  const years = [endYear - 4, endYear - 3, endYear - 2, endYear - 1, endYear];
  const periods = showQuarters ? quarterPeriodLabels(years) : years.map(String);

  const locData = periods
    .map((p) => {
      const v = trendByPeriod?.[p]?.location;
      return typeof v === "number" ? { x: p, y: v } : null;
    })
    .filter(Boolean);
  const mktData = periods
    .map((p) => {
      const v = trendByPeriod?.[p]?.market;
      return typeof v === "number" ? { x: p, y: v } : null;
    })
    .filter(Boolean);

  const series = [];
  if (locData.length) series.push({ id: "Location-based", color: LOCATION_COLOR, data: locData });
  if (mktData.length) series.push({ id: "Market-based", color: MARKET_COLOR, data: mktData });
  return series.length ? series : null;
};

const ScopeTwoTrendChart = ({ latestYear, trendByPeriod, showQuarters = false }) => {
  const series = buildTrendSeries(latestYear, trendByPeriod, showQuarters);
  if (!series) {
    return (
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
        <Text textAlign="center" color="gray.500" fontSize="13px">
          {showQuarters
            ? "No quarterly Scope 2 data — emissions for these years were reported at the yearly level only."
            : "No Scope 2 emissions data has been entered for this year."}
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} h="420px">
        <ResponsiveLine
          data={series}
          margin={{ top: 12, right: 24, bottom: showQuarters ? 76 : 56, left: 64 }}
          xScale={{ type: "point" }}
          yScale={{ type: "linear", min: 0, max: "auto", stacked: false }}
          colors={({ color }) => color}
          curve="monotoneX"
          enableArea
          areaOpacity={0.12}
          // Render lines + points via custom layers (not Nivo's built-in animated
          // points). The built-in point animation desyncs from the line path when
          // the data/x-domain churns on year switching, leaving points stranded
          // away from the line. Mirrors EmissionsTrends.jsx for the same reason.
          enablePoints={false}
          layers={[
            "grid",
            "markers",
            "axes",
            axisBaselineLayer,
            "areas",
            "crosshair",
            ({ series, lineGenerator, xScale, yScale }) => (
              <g>
                {series.map((s) => {
                  const path = lineGenerator(
                    s.data.map((d) => ({ x: xScale(d.data.x), y: yScale(d.data.y) }))
                  );
                  return (
                    <path
                      key={s.id}
                      d={path}
                      fill="none"
                      stroke={s.color}
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  );
                })}
              </g>
            ),
            ({ series, xScale, yScale }) => (
              <g>
                {series.map((s) =>
                  s.data.map((d, i) => (
                    <circle
                      key={`${s.id}-${i}`}
                      cx={xScale(d.data.x)}
                      cy={yScale(d.data.y)}
                      r={3}
                      fill="#FFFFFF"
                      stroke={s.color}
                      strokeWidth={2}
                    />
                  ))
                )}
              </g>
            ),
            "slices",
            "mesh",
          ]}
          axisBottom={{ tickSize: 0, tickPadding: 6, tickRotation: showQuarters ? -45 : 0 }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 6,
            format: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v),
          }}
          enableGridX={false}
          theme={chartTheme}
          legends={[
            {
              anchor: "bottom",
              direction: "row",
              translateY: showQuarters ? 70 : 50,
              itemWidth: 130,
              itemHeight: 18,
              symbolSize: 10,
              symbolShape: "circle",
            },
          ]}
          tooltip={({ point }) => (
            <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
              <Text fontWeight="700" color="gray.800">{point.serieId} · {point.data.x}</Text>
              <Text color={point.serieColor}>
                {Number(point.data.y).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tCO₂e
              </Text>
            </Box>
          )}
          useMesh
        />
      </Box>
      <Text fontSize="11px" color="gray.500" mt={2}>
        {showQuarters
          ? "Quarters without a finalised report are omitted — years reported at the yearly level only will not appear."
          : "Years without a finalised report are omitted from the line."}
      </Text>
    </Box>
  );
};

// Wraps the trend / flow views behind a toggle, mirroring Scope3Visualisation.
// Trend = the 5-year line chart. Flow = a sankey splitting Scope 2 into the two
// accounting methods (Location-based / Market-based) and then into the four
// purchased-electricity types within each.
const ScopeTwoVisualisation = ({ latestYear, trendByYear, trendByQuarter, trendQuarters, setTrendQuarters, v2Categories, hasV2, certificates, hasCertificates, dimensionParams }) => {
  const [view, setView] = useState("trend");
  const isTrend = view === "trend";
  const isTable = view === "table";
  return (
    <Box>
      <Flex justify="space-between" align="flex-end" mb={2} flexWrap="wrap" gap={3}>
        <Box>
          <Text fontSize="22px" fontWeight="700" color="darkTeal">
            {isTrend ? "5-year trend" : isTable ? "Emissions by quarter" : "Emissions flow"}
          </Text>
          <Text fontSize="13px" color="gray.500">
            {isTrend
              ? `Scope 2 location-based and market-based emissions across the last five reporting years${trendQuarters ? ", split by quarter" : ""}.`
              : isTable
                ? `Scope 2 figures for ${latestYear}, split by quarter with a full-year total.`
                : "How Scope 2 splits across the location-based and market-based methods, then into each purchased-electricity type."}
          </Text>
        </Box>
        <HStack spacing={3}>
          {isTrend && (
            <QuartersSwitch isChecked={trendQuarters} onChange={setTrendQuarters} />
          )}
          <ScopeTwoChartViewToggle view={view} setView={setView} />
        </HStack>
      </Flex>
      {isTrend && (
        <ScopeTwoTrendChart
          latestYear={latestYear}
          trendByPeriod={trendQuarters ? trendByQuarter : trendByYear}
          showQuarters={trendQuarters}
        />
      )}
      {isTable && <ScopeTwoQuarterlyTable year={latestYear} dimensionParams={dimensionParams} />}
      {!isTrend && !isTable && (
        <ScopeTwoSankeyChart categories={v2Categories} hasV2={hasV2} certificates={certificates} hasCertificates={hasCertificates} />
      )}
    </Box>
  );
};

// Quarter-by-quarter table of the page's KPI-card figures (Location-based /
// Market-based / Renewable offset). Mounted only while the Table view is
// selected. Offset = max(0, location − market), same as the KPI card.
const ScopeTwoQuarterlyTable = ({ year, dimensionParams }) => {
  const compare = useQuarterlyTableCompare();
  const currentQueries = useGetQuarterlyEmissions(year, dimensionParams, {
    retry: false,
    cacheTime: 60_000,
  });
  const prevQueries = useGetQuarterlyEmissions(year - 1, dimensionParams, {
    retry: false,
    cacheTime: 60_000,
    enabled: compare.needsPreviousYear,
  });
  const payloads = currentQueries.map((q) => (q.status === "success" ? q.data : null));
  const prevPayloads = prevQueries.map((q) => (q.status === "success" ? q.data : null));

  const offsetOf = (d) => {
    const loc = d?.scope2LocationTotal;
    const mkt = d?.scope2MarketTotal;
    return loc != null && mkt != null ? Math.max(0, loc - mkt) : null;
  };
  const ROWS = [
    { label: "Location-based", bold: true, get: (d) => d?.scope2LocationTotal },
    { label: "Market-based", get: (d) => d?.scope2MarketTotal },
    { label: "Renewable offset", get: offsetOf },
  ];
  const rows = ROWS.map((row) => ({
    label: row.label,
    bold: row.bold,
    values: payloads.map((d) => row.get(d)),
    prevValues: prevPayloads.map((d) => row.get(d)),
  }));

  const isLoading =
    currentQueries.some((q) => q.isLoading) ||
    (compare.needsPreviousYear && prevQueries.some((q) => q.isLoading));

  return <QuarterlyBreakdownTable year={year} rows={rows} isLoading={isLoading} compare={compare} />;
};

const ScopeTwoChartViewToggle = ({ view, setView }) => {
  const options = [
    { label: "Trend", value: "trend", icon: <ChartLine size={14} /> },
    { label: "Flow", value: "sankey", icon: <FlowArrow size={14} /> },
    { label: "Table", value: "table", icon: <ListBullets size={14} /> },
  ];
  return (
    <Flex bg="gray.100" borderRadius="full" p="3px">
      {options.map((option) => {
        const active = view === option.value;
        return (
          <Button
            key={option.value}
            size="xs"
            borderRadius="full"
            bg={active ? "pureBlack" : "transparent"}
            color={active ? "white" : "gray.500"}
            fontWeight={active ? "600" : "normal"}
            rightIcon={option.icon}
            onClick={() => setView(option.value)}
            _hover={{ bg: active ? "pureBlack" : "gray.200" }}
            px={3}
          >
            {option.label}
          </Button>
        );
      })}
    </Flex>
  );
};

// Certificate-instrument palette for the Sankey 4th tier (greens, since they
// sit under Certified Renewable).
const CERTIFICATE_COLORS = ["#15803D", "#22C55E", "#4ADE80", "#65A30D", "#A3E635", "#BEF264"];

// Sankey: Scope 2 total → Location-based / Market-based methods → each
// purchased-electricity v2 category within that method → (location branch only)
// each certificate instrument under Certified Renewable. All values are REAL,
// from the per-dimension electricityV2 + certificate breakdowns (ESG-3935).
// Zero-value branches are dropped (sankey rejects zero-weight links). Shown only
// for orgs on Scope 2 v2; v1 orgs get a "Coming soon" panel. The certificate
// tier uses each instrument's persisted location tCO2e (which sums to the
// Certified Renewable location total) — its kWh is shown in the tooltip.
const ScopeTwoSankeyChart = ({ categories, hasV2, certificates, hasCertificates }) => {
  const { nodes, links, labels } = useMemo(() => {
    if (!hasV2) return { nodes: [], links: [], labels: new Map() };
    const locTotal = categories.reduce((acc, c) => acc + (Number(c.location) || 0), 0);
    const mktTotal = categories.reduce((acc, c) => acc + (Number(c.market) || 0), 0);
    if (locTotal <= 0 && mktTotal <= 0) return { nodes: [], links: [], labels: new Map() };

    const builtNodes = [{ id: "Scope 2", nodeColor: "#1A5276" }];
    const builtLinks = [];

    const methods = [
      { id: "Location-based", total: locTotal, color: LOCATION_COLOR, field: "location", suffix: "Loc" },
      { id: "Market-based", total: mktTotal, color: MARKET_COLOR, field: "market", suffix: "Mkt" },
    ];

    methods.forEach((method) => {
      if (method.total <= 0) return;
      builtNodes.push({ id: method.id, nodeColor: method.color });
      builtLinks.push({ source: "Scope 2", target: method.id, value: Number(method.total.toFixed(2)) });

      categories.forEach((cat) => {
        const value = Number(cat[method.field]) || 0;
        if (value <= 0) return;
        // Suffix the leaf id with the method so the two branches don't collapse
        // onto a single shared node (sankey keys nodes by id).
        const label = V2_CATEGORY_LABELS[cat.category] || cat.category;
        const typeId = `${label} (${method.suffix})`;
        builtNodes.push({ id: typeId, nodeColor: V2_CATEGORY_COLORS[cat.category] || method.color });
        builtLinks.push({ source: method.id, target: typeId, value: Number(value.toFixed(2)) });

        // 4th tier (location branch only): split Certified Renewable into its
        // certificate instruments by each instrument's persisted location tCO2e
        // (sums to the category's location total, so the Sankey stays balanced).
        if (method.field === "location" && cat.category === "CERTIFIED_RENEWABLE" && hasCertificates) {
          certificates.forEach((cert, i) => {
            const certValue = Number(cert.location) || 0;
            if (certValue <= 0) return;
            const certId = `${cert.certificateType} (${method.suffix})`;
            builtNodes.push({ id: certId, nodeColor: CERTIFICATE_COLORS[i % CERTIFICATE_COLORS.length], kwh: cert.kwh });
            builtLinks.push({ source: typeId, target: certId, value: Number(certValue.toFixed(2)) });
          });
        }
      });
    });

    return toSafariSafeSankey(builtNodes, builtLinks);
  }, [categories, hasV2, certificates, hasCertificates]);

  if (!hasV2) {
    return (
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
        <Text textAlign="center" color="gray.500" fontSize="13px">
          Coming soon — the emissions flow breakdown is available for Scope 2 v2 questionnaires.
        </Text>
      </Box>
    );
  }

  if (nodes.length === 0) {
    return (
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
        <Text textAlign="center" color="gray.500" fontSize="13px">
          No Scope 2 emissions data has been entered for this year.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} h={{ base: "420px", md: "480px" }}>
        <ResponsiveSankey
          data={{ nodes, links }}
          margin={{ top: 12, right: 150, bottom: 12, left: 110 }}
          align="justify"
          sort="input"
          colors={(node) => node.nodeColor}
          nodeOpacity={1}
          nodeHoverOthersOpacity={0.35}
          nodeThickness={14}
          nodeSpacing={18}
          nodeBorderWidth={0}
          nodeBorderRadius={2}
          linkOpacity={0.45}
          linkHoverOthersOpacity={0.1}
          linkContract={2}
          enableLinkGradient
          labelPosition="outside"
          labelOrientation="horizontal"
          labelPadding={10}
          labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          label={(node) => labels.get(node.id) || node.id}
          theme={chartTheme}
          nodeTooltip={({ node }) => {
            const kwh = node.kwh ?? node.data?.kwh;
            return (
            <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
              <Text fontWeight="700" color="gray.800">{labels.get(node.id) || node.id}</Text>
              <Text color="gray.600">
                {Number(node.value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tCO₂e
              </Text>
              {kwh != null && (
                <Text color="gray.500">
                  {Number(kwh).toLocaleString("en-GB", { maximumFractionDigits: 0 })} kWh
                </Text>
              )}
            </Box>
            );
          }}
          linkTooltip={({ link }) => (
            <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
              <Text fontWeight="700" color="gray.800">
                {(labels.get(link.source.id) || link.source.id)} → {(labels.get(link.target.id) || link.target.id)}
              </Text>
              <Text color="gray.600">
                {Number(link.value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tCO₂e
              </Text>
            </Box>
          )}
        />
      </Box>
      <Text fontSize="11px" color="gray.500" mt={2}>
        Splits show each purchased-electricity category's location- and market-based emissions; Certified
        Renewable splits further into its certificate instruments (hover for kWh).
      </Text>
    </Box>
  );
};

// Coloured-accent KPI card matching the new design. `kind="emission"` treats
// a year-on-year decrease as positive (green); `kind="offset"` flips it so an
// increase is positive (more avoided emissions = better).
const KpiCard = ({ accent, title, subtitle, tco2e, previousTco2e, footerLeading, footerKwh, footerTrailing, loading, kind = "emission" }) => {
  const hasCurrent = typeof tco2e === "number";
  const hasPrev = typeof previousTco2e === "number" && previousTco2e !== 0;
  const pctChange = hasCurrent && hasPrev ? ((tco2e - previousTco2e) / previousTco2e) * 100 : null;
  const isImprovement = pctChange == null
    ? null
    : (kind === "offset" ? pctChange > 0 : pctChange < 0);
  const pillBg = isImprovement === null ? "gray.100" : isImprovement ? "green.50" : "red.50";
  const pillColor = isImprovement === null ? "gray.500" : isImprovement ? "green.700" : "red.600";
  const Arrow = pctChange == null ? null : (pctChange < 0 ? ArrowDown : ArrowUp);

  return (
    <Box position="relative" pt={3} sx={{ "--big": "clamp(20px, 3vw, 56px)" }}>
      <Box position="absolute" top={0} left={0} right={0} h="3px" bg={accent} borderRadius="full" />
      <HStack align="baseline" spacing={2} mb={2}>
        <Text fontSize="15px" fontWeight="700" color="darkTeal">{title}</Text>
        <Text fontSize="13px" color="gray.500">{subtitle}</Text>
      </HStack>
      <HStack align="stretch" spacing={3} flexWrap="nowrap">
        <Text fontSize="var(--big)" fontWeight="700" color="darkTeal" lineHeight="1" letterSpacing="-0.02em" whiteSpace="nowrap">
          {loading ? "…" : (hasCurrent ? formatTco2(tco2e) : "—")}
        </Text>
        <Flex direction="column" justify="space-between" py={0.5} flexShrink={0}>
          {pctChange != null ? (
            <HStack spacing={1} bg={pillBg} color={pillColor} px={2} py={0.5} borderRadius="full" alignSelf="flex-start">
              {Arrow && <Arrow size={11} weight="bold" />}
              <Text fontSize="11px" fontWeight="600">
                {Math.abs(pctChange).toFixed(1)} %
              </Text>
            </HStack>
          ) : <Box />}
          <Text fontSize="calc(var(--big) * 0.32)" fontWeight="600" color="gray.600" lineHeight="1">tCO₂e</Text>
        </Flex>
      </HStack>
      {(footerKwh != null || footerLeading || footerTrailing) && (
        <HStack spacing={2} mt={3} align="baseline" flexWrap="wrap">
          {footerLeading && (
            <Text fontSize="13px" color="gray.500">{footerLeading}</Text>
          )}
          {footerKwh != null && (
            <Text fontSize="22px" fontWeight="700" color="darkTeal" lineHeight="1">
              {formatKwh(footerKwh)}
            </Text>
          )}
          {footerTrailing && (
            <Text fontSize="13px" color="gray.500">{footerTrailing}</Text>
          )}
        </HStack>
      )}
    </Box>
  );
};

// "By dimension" breakdown — REAL per-dimension Scope 2 location + market
// (and summed v2 kWh) from the dimension report (ESG-3935).

const ByDimensionTable = ({ rows = [], levelName, unattributed = null, shownCount = 0, totalCount = null, loading = false, hasNextLevel = true, levelOptions = [], selectedLevelKey = null, autoLevelKey = null, onSelectLevel = null, quarterly = false, setQuarterly = null, quarterlyRows = [], quarterlyLoading = false, year = null }) => {
  // Accordion: expanding a dimension row reveals its own purchased-electricity
  // category breakdown (real per-item electricityV2 data, not a scaled split).
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  const totals = rows.reduce(
    (acc, r) => ({
      location: acc.location + (r.location || 0),
      market: acc.market + (r.market || 0),
      kwh: acc.kwh + (r.kwh || 0),
    }),
    { location: 0, market: 0, kwh: 0 }
  );
  const totalLocationShare = totals.location > 0 ? 1 : 0;
  const totalMarketShare = totals.market > 0 ? 1 : 0;
  // ESG-3960: keyed by dimension item ID — by name, expanding one of two same-name items opened both.
  const rowIds = rows.map((r) => r.id);
  const openCount = rowIds.filter((id) => expanded[id]).length;
  const expandAll = () => setExpanded(Object.fromEntries(rowIds.map((id) => [id, true])));
  const collapseAll = () => setExpanded({});
  // The "By {level}" title is a dropdown so the breakdown level can be chosen independently of the
  // page's dimension filter. "Auto" is the level below the deepest filtered level.
  const levelSelectable = onSelectLevel && levelOptions.length > 0;
  const levelLabel = levelName ? levelName.toLowerCase() : "dimension";
  const title = `By ${levelLabel}`;
  const truncated = totalCount != null && shownCount > 0 && totalCount > shownCount;
  return (
    <Box>
      <Flex justify="space-between" align="flex-end" mb={3} gap={3} flexWrap="wrap">
        <Flex align="center" gap={3}>
          {levelSelectable ? (
            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                px={1}
                h="auto"
                fontSize="22px"
                fontWeight="700"
                color="darkTeal"
                rightIcon={<CaretDown size={16} weight="bold" />}
                _hover={{ bg: "gray.50" }}
                _active={{ bg: "gray.100" }}
              >
                {title}
              </MenuButton>
              <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1}>
                <MenuItem
                  onClick={() => onSelectLevel(null)}
                  fontWeight={selectedLevelKey === autoLevelKey ? "bold" : "normal"}
                >
                  <Flex align="center" gap={2} w="full" justify="space-between">
                    <Text>Auto</Text>
                    <Text fontSize="11px" color="gray.400">
                      level below filter
                    </Text>
                  </Flex>
                </MenuItem>
                {levelOptions.map((opt) => (
                  <MenuItem
                    key={opt.key}
                    onClick={() => onSelectLevel(opt.key)}
                    fontWeight={selectedLevelKey === opt.key ? "bold" : "normal"}
                  >
                    {opt.name}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          ) : (
            <Text fontSize="22px" fontWeight="700" color="darkTeal">{title}</Text>
          )}
        </Flex>
        <HStack spacing={4}>
          {setQuarterly && <QuartersSwitch isChecked={quarterly} onChange={setQuarterly} />}
          {!quarterly && <ShareLegend />}
          <Text fontSize="13px" color="gray.500">
            {quarterly
              ? `${levelName ? `${levelName} · ` : ""}location emissions by quarter, real kWh in brackets.`
              : `${levelName ? `${levelName} · ` : ""}click a row for its electricity type breakdown.`}
          </Text>
        </HStack>
      </Flex>
      {quarterly ? (
        <ScopeTwoByDimensionQuarterlyTable
          levelName={levelName}
          rows={quarterlyRows}
          loading={quarterlyLoading}
          year={year}
        />
      ) : loading ? (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" p={8}>
          <Text textAlign="center" color="gray.500" fontSize="13px">Loading…</Text>
        </Box>
      ) : !hasNextLevel ? (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" p={8}>
          <Text textAlign="center" color="gray.500" fontSize="13px">No further dimension breakdown available.</Text>
        </Box>
      ) : rows.length === 0 ? (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" p={8}>
          {/* ESG-3960: level-aware. An empty table here can mean either "no Scope 2 at all" or "your
              Scope 2 isn't tagged at this level" — and reading the second as the first understates a
              footprint. When there IS unattributed Scope 2, say which it is. */}
          <Text textAlign="center" color="gray.500" fontSize="13px">
            {unattributed != null
              ? `No Scope 2 is assigned to a ${levelLabel}. ${formatTco2(unattributed)} tCO₂e is recorded for this year but carries no ${levelLabel}.`
              : `No Scope 2 emissions recorded against this ${levelLabel} for this year.`}
          </Text>
        </Box>
      ) : (
      <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
        <Table size="sm" variant="unstyled">
          <Thead>
            <Tr bg="gray.50">
              <Th py={3} px={4} colSpan={2} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">
                <HStack spacing={2}>
                  <Text as="span">Dimension</Text>
                  {rowIds.length > 0 && (
                    <ExpandCollapseControl
                      allExpanded={openCount === rowIds.length}
                      onExpandAll={expandAll}
                      onCollapseAll={collapseAll}
                    />
                  )}
                </HStack>
              </Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Consumption (kWh)</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Location Emissions (tCO₂e)</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Market Based (tCO₂e)</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Share of Scope 2</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, idx) => {
              const locationShare = totals.location > 0 ? row.location / totals.location : 0;
              const marketShare = totals.market > 0 ? row.market / totals.market : 0;
              const canExpand = (row.categories || []).length > 0;
              const isOpen = !!expanded[row.id];
              return (
                <Fragment key={row.id}>
                  <Tr
                    borderTop={idx === 0 ? "none" : "1px solid"}
                    borderColor="gray.100"
                    cursor={canExpand ? "pointer" : "default"}
                    _hover={canExpand ? { bg: "gray.50" } : undefined}
                    onClick={canExpand ? () => toggle(row.id) : undefined}
                  >
                    <Td py={4} pl={4} pr={1} width="32px">
                      {canExpand && (
                        <IconButton
                          size="xs"
                          variant="ghost"
                          aria-label={isOpen ? "Collapse" : "Expand"}
                          icon={isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                          onClick={(e) => { e.stopPropagation(); toggle(row.id); }}
                        />
                      )}
                    </Td>
                    <Td py={4} pl={1} pr={4} fontSize="14px" color="gray.700" fontWeight="500" textAlign="left">{row.name}</Td>
                    <Td py={4} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">{row.kwh > 0 ? formatKwh(row.kwh) : "—"}</Td>
                    <Td py={4} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">{formatTco2(row.location)}</Td>
                    <Td py={4} px={4} fontSize="14px" color="limeShade.700" fontWeight="600" textAlign="right">{formatTco2(row.market)}</Td>
                    <Td py={4} px={4} textAlign="left" minW="240px">
                      <DualShareBar locationShare={locationShare} marketShare={marketShare} />
                    </Td>
                  </Tr>
                  {isOpen && (
                    <>
                      {/* Sub-header anchored to the parent's columns so the
                          category rows line up with kWh / Location / Market. */}
                      <Tr bg="gray.50">
                        <Td py={2} pl={4} pr={1} width="32px" />
                        <Td py={2} pl={1} pr={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Electricity type</Td>
                        <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Consumption (kWh)</Td>
                        <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Location Emissions (tCO₂e)</Td>
                        <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Market Based (tCO₂e)</Td>
                        <Td py={2} px={4} />
                      </Tr>
                      {row.categories.map((cat) => (
                        <Tr key={`${row.id}-${cat.category}`} bg="gray.50">
                          <Td py={2} pl={4} pr={1} width="32px" />
                          <Td py={2} pl={1} pr={4} fontSize="13px" color="gray.700" textAlign="left">
                            <HStack spacing={2}>
                              <Box w="8px" h="8px" bg={V2_CATEGORY_COLORS[cat.category] || "gray.400"} borderRadius="sm" />
                              <Text fontWeight="500">{V2_CATEGORY_LABELS[cat.category] || cat.category}</Text>
                            </HStack>
                          </Td>
                          <Td py={2} px={4} fontSize="13px" color="gray.700" fontWeight="600" textAlign="right">{cat.kwh > 0 ? formatKwh(cat.kwh) : "—"}</Td>
                          <Td py={2} px={4} fontSize="13px" color="gray.700" fontWeight="600" textAlign="right">{formatTco2(cat.location)}</Td>
                          <Td py={2} px={4} fontSize="13px" color="limeShade.700" fontWeight="600" textAlign="right">{formatTco2(cat.market)}</Td>
                          <Td py={2} px={4} />
                        </Tr>
                      ))}
                    </>
                  )}
                </Fragment>
              );
            })}
            {/* ESG-3960: Scope 2 that carries no dimension item at this level, so it belongs to none of
                the rows above. Its own row rather than folded into Total — it is an absence. */}
            {unattributed != null && (
              <Tr borderTop="1px solid" borderColor="gray.100" bg="orange.50">
                <Td py={4} pl={4} pr={1} width="32px" />
                <Td py={4} pl={1} pr={4} fontSize="14px" color="gray.600" fontStyle="italic" textAlign="left">
                  {`No ${levelLabel} assigned`}
                </Td>
                <Td py={4} px={4} textAlign="right" color="gray.400" fontSize="14px">—</Td>
                <Td py={4} px={4} fontSize="14px" color="gray.600" fontWeight="700" textAlign="right">{formatTco2(unattributed)}</Td>
                <Td py={4} px={4} textAlign="right" color="gray.400" fontSize="14px">—</Td>
                <Td py={4} px={4} textAlign="left" minW="240px">
                  <Text fontSize="11px" color="gray.500">Scope 2 not attributed at this level</Text>
                </Td>
              </Tr>
            )}
            <Tr bg="gray.50" borderTop="2px solid" borderColor="gray.200">
              <Td py={4} pl={4} pr={1} width="32px" />
              <Td py={4} pl={1} pr={4} fontSize="13px" color="darkTeal" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" textAlign="left">Total</Td>
              <Td py={4} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{totals.kwh > 0 ? formatKwh(totals.kwh) : "—"}</Td>
              <Td py={4} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{formatTco2(totals.location)}</Td>
              <Td py={4} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{formatTco2(totals.market)}</Td>
              <Td py={4} px={4} textAlign="left" minW="240px">
                <DualShareBar locationShare={totalLocationShare} marketShare={totalMarketShare} />
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>
      )}
    </Box>
  );
};

// Quarter-by-quarter variant of the Scope 2 by-dimension table.
// One row per dimension item; columns Q1..Q4 + a whole-year total. Each cell
// shows LOCATION-based emissions with real kWh bracketed. All figures are
// exact (real per-dimension electricityV2 per period — no apportionment).
// The year total is the ALL-period figure and may exceed ΣQ1..Q4 when data
// was entered yearly. Rows expand to their per-electricity-type split.
const S2_PERIOD_HEADS = ["Q1", "Q2", "Q3", "Q4", "Year"];
const fmtS2QuarterCell = (p) => {
  if (!p || !(p.location > 0)) return "—";
  const emis = formatTco2(p.location);
  const kwh = p.kwh > 0 ? formatKwh(p.kwh) : null;
  return kwh ? `${emis} (${kwh})` : emis;
};
const ScopeTwoByDimensionQuarterlyTable = ({ levelName, rows = [], loading = false, year = null }) => {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  // ESG-3960: keyed by dimension item ID, as above.
  const expandableIds = rows.filter((r) => (r.categories || []).length > 0).map((r) => r.id);
  const openCount = expandableIds.filter((id) => expanded[id]).length;
  const expandAll = () => setExpanded(Object.fromEntries(expandableIds.map((id) => [id, true])));
  const collapseAll = () => setExpanded({});

  const colTotals = S2_PERIOD_HEADS.map((_, pi) =>
    rows.reduce(
      (acc, r) => ({ location: acc.location + (r.periods[pi]?.location || 0), kwh: acc.kwh + (r.periods[pi]?.kwh || 0) }),
      { location: 0, kwh: 0 }
    )
  );

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
      <Table size="sm" variant="unstyled">
        <Thead>
          <Tr bg="gray.50">
            <Th py={3} px={4} colSpan={2} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">
              <HStack spacing={2}>
                <Text as="span">{levelName ? levelName.toUpperCase() : "DIMENSION"}</Text>
                {expandableIds.length > 0 && (
                  <ExpandCollapseControl
                    allExpanded={openCount === expandableIds.length}
                    onExpandAll={expandAll}
                    onCollapseAll={collapseAll}
                  />
                )}
              </HStack>
            </Th>
            {S2_PERIOD_HEADS.map((h) => (
              <Th key={h} py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">
                {h === "Year" ? `${year || ""} Total` : h}
                <Text as="span" fontSize="9px" color="gray.400" fontWeight="500" textTransform="none"> tCO₂e (kWh)</Text>
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <Tr><Td colSpan={7} py={6} textAlign="center" color="gray.400" fontSize="13px">Loading…</Td></Tr>
          ) : rows.length === 0 ? (
            <Tr><Td colSpan={7} py={6} textAlign="center" color="gray.400" fontSize="13px">No quarterly Scope 2 data for this year.</Td></Tr>
          ) : (
            rows.map((row, idx) => {
              const hasCats = (row.categories || []).length > 0;
              const isOpen = hasCats && !!expanded[row.id];
              return (
                <Fragment key={row.id}>
                  <Tr
                    borderTop={idx === 0 ? "none" : "1px solid"}
                    borderColor="gray.100"
                    cursor={hasCats ? "pointer" : "default"}
                    _hover={hasCats ? { bg: "gray.50" } : undefined}
                    onClick={hasCats ? () => toggle(row.id) : undefined}
                  >
                    <Td py={4} pl={4} pr={1} width="32px">
                      {hasCats && (
                        <IconButton
                          size="xs"
                          variant="ghost"
                          aria-label={isOpen ? "Collapse" : "Expand"}
                          icon={isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                          onClick={(e) => { e.stopPropagation(); toggle(row.id); }}
                        />
                      )}
                    </Td>
                    <Td py={4} pl={1} pr={4} fontSize="14px" color="gray.700" fontWeight="500" textAlign="left">{row.name}</Td>
                    {row.periods.map((p, pi) => (
                      <Td key={pi} py={4} px={4} fontSize="13px" fontWeight={pi === 4 ? "700" : "600"} color={pi === 4 ? "darkTeal" : "gray.700"} textAlign="right">
                        {fmtS2QuarterCell(p)}
                      </Td>
                    ))}
                  </Tr>
                  {isOpen && (
                    <>
                      <Tr bg="gray.50">
                        <Td py={2} pl={4} pr={1} width="32px" />
                        <Td py={2} pl={1} pr={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="700" textAlign="left">Electricity type</Td>
                        {S2_PERIOD_HEADS.map((h) => (
                          <Td key={h} py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="700" textAlign="right">
                            {h === "Year" ? "Total" : h}
                          </Td>
                        ))}
                      </Tr>
                      {row.categories.map((cat) => (
                        <Tr key={`${row.id}-${cat.category}`} bg="gray.50">
                          <Td py={2.5} pl={4} pr={1} width="32px" />
                          <Td py={2.5} pl={1} pr={4} fontSize="13px" color="gray.600" textAlign="left">
                            <HStack spacing={2}>
                              <Box w="8px" h="8px" bg={V2_CATEGORY_COLORS[cat.category] || "gray.400"} borderRadius="sm" />
                              <Text>{V2_CATEGORY_LABELS[cat.category] || cat.category}</Text>
                            </HStack>
                          </Td>
                          {cat.periods.map((p, pi) => (
                            <Td key={pi} py={2.5} px={4} fontSize="12px" fontWeight={pi === 4 ? "700" : "600"} color={pi === 4 ? "darkTeal" : "gray.600"} textAlign="right">
                              {fmtS2QuarterCell(p)}
                            </Td>
                          ))}
                        </Tr>
                      ))}
                    </>
                  )}
                </Fragment>
              );
            })
          )}
          {!loading && rows.length > 0 && (
            <Tr bg="gray.50" borderTop="2px solid" borderColor="gray.200">
              <Td py={4} px={4} colSpan={2} fontSize="13px" color="darkTeal" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" textAlign="left">Total</Td>
              {colTotals.map((t, pi) => (
                <Td key={pi} py={4} px={4} fontSize="13px" color="darkTeal" fontWeight="700" textAlign="right">
                  {t.location > 0 ? fmtS2QuarterCell(t) : "—"}
                </Td>
              ))}
            </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );
};

// "By purchased electricity type" — the Scope 2 v2 purchased-electricity
// categories. REAL location/market/kWh per category from the aggregated
// electricityV2 breakdown (ESG-3935). Empty for v1 orgs → "Coming soon".
const PurchasedTypeTable = ({ categories = [], hasV2 = false, certificates = [] }) => {
  // Exclude ONSITE here — it's surfaced in its own highlight below.
  const rows = (categories || []).filter((c) => c.category !== "ONSITE");
  const totals = rows.reduce(
    (acc, r) => ({
      kwh: acc.kwh + (r.kwh || 0),
      location: acc.location + (r.location || 0),
      market: acc.market + (r.market || 0),
    }),
    { kwh: 0, location: 0, market: 0 }
  );

  // Certified Renewable can roll down to its per-certificate-instrument
  // breakdown — the same 4th tier the Sankey shows. Backend only decomposes
  // kWh + location per instrument (market isn't decomposable), so the market
  // column shows a dash on the sub-rows. Sorted by kWh desc.
  const certRows = (certificates || [])
    .filter((c) => c.kwh > 0)
    .sort((a, b) => b.kwh - a.kwh);
  const [certOpen, setCertOpen] = useState(false);

  return (
    <Box>
      <Flex justify="space-between" align="flex-end" mb={3} gap={3} flexWrap="wrap">
        <Text fontSize="22px" fontWeight="700" color="darkTeal">By purchased electricity type</Text>
        <ShareLegend />
      </Flex>
      {!hasV2 || rows.length === 0 ? (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" p={8}>
          <Text textAlign="center" color="gray.500" fontSize="13px">
            Coming soon — the per-category breakdown is available for Scope 2 v2 questionnaires.
          </Text>
        </Box>
      ) : (
      <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
        <Table size="sm" variant="unstyled">
          <Thead>
            <Tr bg="gray.50">
              <Th py={3} px={4} colSpan={2} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Electricity Type</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Consumption (kWh)</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Location Emissions (tCO₂e)</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Market Based (tCO₂e)</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Share of Scope 2</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, idx) => {
              const locationShare = totals.location > 0 ? row.location / totals.location : 0;
              const marketShare = totals.market > 0 ? row.market / totals.market : 0;
              const canExpand = row.category === "CERTIFIED_RENEWABLE" && certRows.length > 0;
              return (
                <Fragment key={row.category}>
                <Tr
                  borderTop={idx === 0 ? "none" : "1px solid"}
                  borderColor="gray.100"
                  cursor={canExpand ? "pointer" : "default"}
                  _hover={canExpand ? { bg: "gray.50" } : undefined}
                  onClick={canExpand ? () => setCertOpen((o) => !o) : undefined}
                >
                  <Td py={4} pl={4} pr={1} width="32px">
                    {canExpand && (
                      <IconButton
                        size="xs"
                        variant="ghost"
                        aria-label={certOpen ? "Collapse" : "Expand"}
                        icon={certOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                        onClick={(e) => { e.stopPropagation(); setCertOpen((o) => !o); }}
                      />
                    )}
                  </Td>
                  <Td py={4} pl={1} pr={4} fontSize="14px" color="gray.700" fontWeight="500" textAlign="left">{V2_CATEGORY_LABELS[row.category] || row.category}</Td>
                  <Td py={4} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">{row.kwh > 0 ? formatKwh(row.kwh) : "—"}</Td>
                  <Td py={4} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">{formatTco2(row.location)}</Td>
                  <Td py={4} px={4} fontSize="14px" color="limeShade.700" fontWeight="600" textAlign="right">{formatTco2(row.market)}</Td>
                  <Td py={4} px={4} textAlign="left" minW="240px">
                    <DualShareBar locationShare={locationShare} marketShare={marketShare} />
                  </Td>
                </Tr>
                {canExpand && certOpen && (
                  <>
                    <Tr bg="gray.50">
                      <Td py={2} pl={4} pr={1} width="32px" />
                      <Td py={2} pl={1} pr={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Certificate instrument</Td>
                      <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Consumption (kWh)</Td>
                      <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Location Emissions (tCO₂e)</Td>
                      <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Market Based (tCO₂e)</Td>
                      <Td py={2} px={4} />
                    </Tr>
                    {certRows.map((cert) => (
                      <Tr key={cert.certificateType} bg="gray.50">
                        <Td py={2.5} pl={4} pr={1} width="32px" />
                        <Td py={2.5} pl={1} pr={4} fontSize="13px" color="gray.600" textAlign="left">
                          <HStack spacing={2}>
                            <Box w="8px" h="8px" bg={V2_CATEGORY_COLORS.CERTIFIED_RENEWABLE || "gray.400"} borderRadius="sm" />
                            <Text>{cert.certificateType}</Text>
                          </HStack>
                        </Td>
                        <Td py={2.5} px={4} fontSize="13px" color="gray.600" fontWeight="600" textAlign="right">{cert.kwh > 0 ? formatKwh(cert.kwh) : "—"}</Td>
                        <Td py={2.5} px={4} fontSize="13px" color="gray.600" fontWeight="600" textAlign="right">{formatTco2(cert.location)}</Td>
                        <Td py={2.5} px={4} fontSize="13px" color="gray.400" fontWeight="600" textAlign="right">—</Td>
                        <Td py={2.5} px={4} />
                      </Tr>
                    ))}
                  </>
                )}
                </Fragment>
              );
            })}
            <Tr bg="gray.50" borderTop="2px solid" borderColor="gray.200">
              <Td py={4} px={4} colSpan={2} fontSize="13px" color="darkTeal" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" textAlign="left">Total</Td>
              <Td py={4} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{totals.kwh > 0 ? formatKwh(totals.kwh) : "—"}</Td>
              <Td py={4} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{formatTco2(totals.location)}</Td>
              <Td py={4} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{formatTco2(totals.market)}</Td>
              <Td py={4} px={4} textAlign="left" minW="240px">
                <DualShareBar locationShare={totals.location > 0 ? 1 : 0} marketShare={totals.market > 0 ? 1 : 0} />
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>
      )}
    </Box>
  );
};

// Compact highlight of on-site generation that contributes to Scope 2. REAL
// figures from the ONSITE v2 category (ESG-3935): self-generated kWh and its
// location/market CO2e. Full onsite detail lives on the Consumption dashboard.
const OnsiteInScopeTwoHighlight = ({ onsite, onOpenConsumption }) => {
  const generatedKwh = Number(onsite?.kwh) || 0;
  const marketTco2 = Number(onsite?.market) || 0;

  return (
    <Box bg="lightLime" border="1px solid" borderColor="limeShade.200" borderRadius="md" p={5}>
      <Flex justify="space-between" align="center" gap={4} flexWrap="wrap">
        <Box>
          <Text fontSize="11px" fontWeight="700" letterSpacing="0.06em" color="limeShade.700" textTransform="uppercase" mb={1}>
            Onsite generation in Scope 2
          </Text>
          <Text fontSize="13px" color="gray.600" maxW="520px">
            Self-generated electricity that contributes to your Scope 2 reporting.{" "}
            {onOpenConsumption ? (
              <Text
                as="button"
                type="button"
                onClick={onOpenConsumption}
                color="limeShade.700"
                fontWeight="600"
                textDecoration="underline"
                textUnderlineOffset="2px"
                _hover={{ color: "limeShade.800" }}
              >
                View full onsite detail on the Consumption dashboard
              </Text>
            ) : (
              "Full onsite detail is on the Consumption dashboard."
            )}
          </Text>
        </Box>
        <Box textAlign="right">
          <Flex align="baseline" gap={2} justify="flex-end">
            <Text fontSize="36px" fontWeight="700" color="darkTeal" lineHeight="1" letterSpacing="-0.02em">
              {generatedKwh > 0 ? formatKwh(generatedKwh) : "—"}
            </Text>
            <Text fontSize="15px" fontWeight="600" color="gray.600">kWh</Text>
          </Flex>
          <Text fontSize="12px" color="gray.500" mt={1}>
            {formatTco2(marketTco2)} tCO₂e market-based
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};
export default ScopeTwoDetail;
